import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';

import { ClickableScenarioInsightTooltip } from '../clickable-scenario-insight-tooltip/ClickableScenarioInsightTooltip';
import { useInsightsCellData } from '../hooks/useInsightsCellData';
import { InsightsIcon } from '../insights-icon/InsightsIcon';
import styles from './InsightsScenarioCell.module.scss';

const MAX_SCENARIO_INSIGHTS = 4;

export type InsightsScenarioCellProps = {
  entityTag: string;
  isDisabled: boolean;
  source: PosthogTrackingSource;
};

export const InsightsScenarioCell = ({
  entityTag,
  isDisabled,
  source,
}: InsightsScenarioCellProps) => {
  const { isLoading, filteredScenarioInsights } = useInsightsCellData({
    entityTag,
  });

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              height: '20px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  const visibleScenarioInsights =
    filteredScenarioInsights.length >= MAX_SCENARIO_INSIGHTS
      ? filteredScenarioInsights.slice(0, MAX_SCENARIO_INSIGHTS - 2)
      : filteredScenarioInsights.slice(0, MAX_SCENARIO_INSIGHTS);

  const remainingScenarioInsights =
    filteredScenarioInsights.length >= MAX_SCENARIO_INSIGHTS
      ? filteredScenarioInsights.slice(MAX_SCENARIO_INSIGHTS - 2)
      : filteredScenarioInsights.slice(MAX_SCENARIO_INSIGHTS);

  return (
    <div className={styles.insightsContainer}>
      {visibleScenarioInsights.map(insight => (
        <InsightsIcon
          key={insight.id}
          insight={insight}
          isDisabled={isDisabled}
          variant="assets-table"
          source={source}
        />
      ))}
      {!!remainingScenarioInsights.length && (
        <TooltipContainer
          label={
            <ClickableScenarioInsightTooltip
              insights={remainingScenarioInsights}
              entityTag={entityTag}
              source={source}
            />
          }
          inPortal
          trigger={[TooltipTrigger.Hover]}
          placement="top-end"
          withSafePolygon
          disabled={isDisabled}
        >
          <div className={isDisabled ? styles.countDisabled : styles.count}>
            +{remainingScenarioInsights.length}
          </div>
        </TooltipContainer>
      )}
    </div>
  );
};
